import { useEffect } from 'react';
import { toNextTrack, toPrevTrack } from '@store/audio/AudioController';
import {
  audioEngineSeekBy,
  setVolumeBy,
  toggleMute,
  togglePlayPause,
} from '@store/audio/AudioEngineHTML5';
import { cycleRepeatMode } from '@store/audio/AudioMeta';
import { toggleShuffleEnabled } from '@store/audio/AudioQueue';

interface KeyboardShortcut {
  key: string;
  command?: boolean;
  control?: boolean;
  option?: boolean;
  shift?: boolean;
  callback: () => void;
}

const shorcuts: KeyboardShortcut[] = [
  {
    // Space to toggle playing
    key: ' ',
    callback: togglePlayPause,
  },
  {
    // r to cycle the repeat mode
    key: 'r',
    callback: cycleRepeatMode,
  },
  {
    // s to toggle shuffle
    key: 's',
    callback: toggleShuffleEnabled,
  },
  {
    // m to toggle mute
    key: 'm',
    callback: toggleMute,
  },
  {
    // OPT + Right to go to next track
    key: 'ArrowRight',
    option: true,
    callback: toNextTrack,
  },
  {
    // OPT + Left to go to prev track
    key: 'ArrowLeft',
    option: true,
    callback: toPrevTrack,
  },
  {
    // OPT + Up to increase volume
    key: 'ArrowUp',
    option: true,
    callback: () => setVolumeBy(0.1),
  },
  {
    // OPT + Down to decrease volume
    key: 'ArrowDown',
    option: true,
    callback: () => setVolumeBy(-0.1),
  },
  {
    // OPT + SHIFT + Left to seek back 5s
    key: 'ArrowLeft',
    option: true,
    shift: true,
    callback: () => audioEngineSeekBy(-5),
  },
  {
    // OPT + SHIFT + Right to seek forward 5s
    key: 'ArrowRight',
    option: true,
    shift: true,
    callback: () => audioEngineSeekBy(5),
  },
];

const INPUT_TAGNAMES = ['INPUT', 'TEXTAREA'];

const handleKey = (event: KeyboardEvent) => {
  // Ignore shortcuts when user is typing
  const target = event.target as HTMLElement;
  if (
    INPUT_TAGNAMES.includes(target.tagName) ||
    target.attributes.getNamedItem('contenteditable')
  ) {
    return;
  }

  for (const { key, command, control, option, shift, callback } of shorcuts) {
    if (
      event.key === key &&
      event.metaKey === !!command &&
      event.ctrlKey === !!control &&
      event.altKey === !!option &&
      event.shiftKey === !!shift
    ) {
      event.stopPropagation();
      callback();
      return;
    }
  }
};

export const useKeyboardShortcuts = () => {
  useEffect(() => {
    document.addEventListener('keydown', handleKey, true);

    return () => document.removeEventListener('keydown', handleKey, true);
  });
};
